import React, { useState, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Container, Button, TextField, Box, InputAdornment, IconButton, Alert } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import ImageUploader from '../Layout/ImageUploader';
import { useAuth } from '../../contexts/AuthContext';
import { useEffect } from 'react';
import { validatePassword, getPasswordStrength } from '../../utils/passwordValidation';
import { getImageDisplayUrl, getUploadApiBaseUrl } from '../../utils/api';

const Register = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState(''); // Nytt
  const [lastName, setLastName] = useState(''); // Nytt
  const [bio, setBio] = useState(''); // Nytt
  const [profilePicture, setProfilePicture] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedFileInfo, setUploadedFileInfo] = useState(null); // Nytt
  const [uploadedImages, setUploadedImages] = useState([]); // Add this line

  const [error, setError] = useState(null);
  const [passwordError, setPasswordError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const { currentUser, userRole } = useAuth();

  useEffect(() => {
    // Lägg till en kontroll av att användaren är inloggad och har rollen admin
    if (userRole !== 'admin') {
      navigate('/'); // omdirigera till hemsidan
      // alternativt kan du visa ett felmeddelande här
    }
  }, [userRole, navigate]);

  // Uppdatera lösenordsvalidering när lösenordet ändras
  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    
    if (newPassword.length === 0) {
      setPasswordError('');
      setPasswordStrength('');
      return;
    }
    
    const errors = validatePassword(newPassword);
    
    if (errors.length > 0) {
      setPasswordError(errors.join(', '));
      setPasswordStrength('weak');
    } else {
      setPasswordError('');
      setPasswordStrength(getPasswordStrength(newPassword));
    }
    
    // Kontrollera matchning om bekräftelselösenordet redan är ifyllt
    if (confirmPassword && newPassword !== confirmPassword) {
      setError('Lösenorden matchar inte');
    } else if (confirmPassword && newPassword === confirmPassword) {
      setError(null);
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);
    
    if (newConfirmPassword && newConfirmPassword !== password) {
      setError('Lösenorden matchar inte');
    } else {
      setError(null);
    }
  };


  const register = async (e) => {
    e.preventDefault();
    setError(null);

    // Validera lösenord
    const passwordErrors = validatePassword(password);
    if (passwordErrors.length > 0) {
      setError(`Lösenordet uppfyller inte kraven: ${passwordErrors.join(', ')}`);
      return;
    }

    // Kontrollera att lösenorden matchar
    if (password !== confirmPassword) {
      setError('Lösenorden matchar inte');
      return;
    }

    let profileUrls = [];
    if (selectedFiles.length > 0) {
      try {
        const formData = new FormData();
        selectedFiles.forEach((file, index) => {
          formData.append(`file${index}`, file);
        });
        const uploadResponse = await axios.post(`${getUploadApiBaseUrl()}/api/uploads/profile-pictures`, formData);
        if (uploadResponse.status === 200) {
          profileUrls = (uploadResponse.data || []).map(f => f.url).filter(Boolean);
          setUploadedImages(profileUrls);
        }
      } catch (err) {
        // Profilbild kräver inloggning – registrera utan profilbild, användaren kan lägga till efter inloggning
      }
    }

    try {
      const _id = (Math.floor(Math.random() * 1e12) + 1e12).toString().substring(1);
      const signupResponse = await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/signup`, {
        _id,
        username,
        email,
        role: 'user',
        password,
        firstName,
        lastName,
        bio,
        profilePictures: profileUrls,
      });
      if (signupResponse.status === 201) {
        navigate('/info');
      } else {
        setError(signupResponse.data?.message || 'Registrering misslyckades.');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Något gick fel.');
    }
  };

  return (
    <div>
      <Container>
        <h2>Skapa ett konto</h2>
        <form onSubmit={register}>
          <Box mb={2}>
            <TextField
              type="text"
              label="Användarnamn"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                if (error) setError(null);
              }}
              required
              sx={{ minWidth: '350px' }}
            />
          </Box>
          <Box mb={2}>
            <TextField
              type="email"
              label="E-post"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError(null);
              }}
              required
              sx={{ minWidth: '350px' }}
            />
          </Box>
          <Box mb={2}>
            <TextField
              type={showPassword ? 'text' : 'password'}
              label="Lösenord"
              value={password}
              onChange={handlePasswordChange}
              required
              error={!!passwordError}
              helperText={
                passwordError 
                  ? passwordError 
                  : passwordStrength === 'strong' 
                    ? 'Starkt lösenord ✓' 
                    : passwordStrength === 'medium' 
                      ? 'Medelstarkt lösenord' 
                      : passwordStrength === 'good' 
                        ? 'Bra lösenord' 
                        : 'Lösenordet måste innehålla: minst 8 tecken, stor bokstav, liten bokstav, siffra och specialtecken'
              }
              sx={{ minWidth: '350px' }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box mb={2}>
            <TextField
              type={showConfirmPassword ? 'text' : 'password'}
              label="Bekräfta lösenord"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              required
              error={!!error && error.includes('matchar inte')}
              helperText={error && error.includes('matchar inte') ? error : ''}
              sx={{ minWidth: '350px' }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box mb={2}>
            <TextField
              type="text"
              label="Förnamn"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              sx={{ minWidth: '350px' }}
            />
          </Box>
          <Box mb={2}>
            <TextField
              type="text"
              label="Efternamn"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              sx={{ minWidth: '350px' }}
            />
          </Box>
          <Box mb={2}>
            <TextField
              multiline
              rows={8}
              label="Skriv lite om båten och besättningen ..."
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              sx={{ minWidth: '350px' }}
            />
          </Box>
          <Box mb={2}>
            <ImageUploader onUploadSuccess={setSelectedFiles} />
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around' }}>
              {uploadedImages.map((imageUrl, index) => (
                <img key={index} src={getImageDisplayUrl(imageUrl)} alt="Uppladdad bild" style={{ width: '30%', height: 'auto', margin: '1%' }} />
              ))}
            </div>
          </Box>
          <Box mb={2}>
            <Button type="submit"
              variant="contained"
              color="primary">
              Registrera kontot
            </Button>
          </Box>
        </form>
        {error && !error.includes('matchar inte') && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Container>
    </div>
  );
}

export default Register;